import {
  EditTransactionField,
  EditTransactionFieldStatus,
  EditTransactionViewModel,
} from '../../edit-transaction/edit-transaction.projection'

/**
 * Layout helpers for the Create/Edit Transaction dialog.
 *
 * The field list is rendered as HTML, not as a grid: every row carries its own control (typeahead,
 * enum picker, date, free text) and its own container, which is a form. What makes it *look* like a
 * grid is that the columns are fixed-width, so rows align with one another without any shared layout
 * container at all.
 *
 * Per-row RULES are a form's other half, and Phase 1 ships one of them: a field's parameterised Req
 * rule (a minimum word count) is judged per row, by the projection's `requiredRuleUnmet`. The rest of
 * validation — legal instrument, account that exists, date inside the period — needs the save-side
 * endpoint and is not implemented here; see the modal's `onEdit`.
 *
 * Collections are not a grid either: their lines are edited as repeating rows in their own modal
 * (`collection-items-modal`), following the Break Config rule editor in recon-configuration-ui.
 * Lines are added and removed far more than they are scanned, which is the opposite of what a grid
 * is for.
 *
 * Kept as pure functions beside the component so the layout stays testable without a TestBed, the
 * same reason `projectEditTransaction` is pure.
 */

/** A section heading, or one field. Section headings span every column of the field grid. */
export type EditTransactionRow =
  | { kind: 'section'; name: string }
  | { kind: 'field'; field: EditTransactionField }

/** Status dot glyph + tone per status, shared by the row column and the summary bar. */
export const STATUS_META: Readonly<
  Record<
    EditTransactionFieldStatus,
    { icon: string; tone: string; label: string }
  >
> = {
  match: {
    icon: 'mdi-check-circle',
    tone: 'et-status-match',
    label: 'No Difference',
  },
  diff: {
    icon: 'mdi-alert-circle',
    tone: 'et-status-diff',
    label: 'Difference',
  },
  partial: { icon: 'mdi-alert', tone: 'et-status-partial', label: 'Partial' },
  novalue: {
    icon: 'mdi-close-circle',
    tone: 'et-status-novalue',
    label: 'No Value',
  },
}

/**
 * Which control a field edits with.
 *
 *  - `enum`   — a closed set, resolved: a picker.
 *  - `lookup` — a reference set whose endpoint is unconfirmed, so it degrades to free text with a
 *               search affordance rather than an empty picker. This is the metadata layer's own
 *               fallback rule, not a rendering decision.
 *  - `date`   — a date input.
 *  - `number` — a numeric input.
 *  - `text`   — everything else.
 */
export type EditTransactionControl =
  | 'enum'
  | 'lookup'
  | 'date'
  | 'number'
  | 'text'

/**
 * Takes the minimal shape rather than a whole field, so a collection's COLUMN resolves its control
 * by the same rules as a top-level field — a distribution line's currency is an enum in the items
 * modal for exactly the reason it would be in the dialog.
 */
export type ControlSource = Pick<
  EditTransactionField,
  'dataType' | 'listType' | 'listValues'
>

/**
 * Chosen from the field's List Type and Data Type — never from its Display Style.
 *
 * Style is formatting and says nothing about what may be typed: a `prettyNumber` is still a number
 * and a `normal` date is still a date. Deciding the control from style is what made every field a
 * plain text box the moment the styles were corrected to the design's three.
 *
 * ONE options-shaped branch, and no knowledge of where the options came from. The metadata document
 * distinguishes a `static` list (authored, resolvable now) from a `catalog`/`search` one (the resolve
 * endpoint's, not wired yet), and the second renders DISABLED — but that is the projection's answer,
 * carried on `EditTransactionField.listPending` and acted on by the templates. Teaching this function
 * about `mode` would make "which control" and "is it usable yet" one decision, and the first is the
 * only question it is asked: a resolved list is a picker, however it was resolved.
 */
export function controlFor(field: ControlSource): EditTransactionControl {
  // An `enum` field is a picker whether or not its options have arrived. A catalog-backed list
  // whose resolve endpoint is not wired yet opens EMPTY rather than degrading to a text box: the
  // field is still a closed set, and offering free text on one invites a value it cannot accept.
  if (field.listValues?.length || field.listType === 'enum') return 'enum'
  if (field.listType === 'lookup') return 'lookup'
  if (field.dataType === 'date') return 'date'
  if (field.dataType === 'number' || field.dataType === 'integer')
    return 'number'
  return 'text'
}

/**
 * Flatten the view model's sections into rows, merging sections that share a name.
 *
 * The metadata can land a schema's own fields in a group the common core already uses — an Interest
 * Rate Swap has "Trade details" from both halves — and the prototype renders that as the same
 * heading twice. Merging is the one place this deliberately improves on it: a heading repeated
 * mid-scroll reads as a rendering fault, not as two groups.
 */
export function buildRows(vm: EditTransactionViewModel): EditTransactionRow[] {
  const order: string[] = []
  const bySection = new Map<string, EditTransactionField[]>()
  vm.sections.forEach(section => {
    if (!bySection.has(section.name)) {
      bySection.set(section.name, [])
      order.push(section.name)
    }
    bySection.get(section.name)?.push(...section.fields)
  })

  return order.flatMap<EditTransactionRow>(name => [
    { kind: 'section', name },
    ...(bySection.get(name) ?? []).map(
      field => ({ kind: 'field', field } as const),
    ),
  ])
}
